import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from "@angular/core";

import { Subscription } from "rxjs";

import { CTAToken, Media } from "@hermes/api-model-editorial";
import { LOCALE } from "@hermes/app-core";

import { AnalyticsService } from "@hermes/utils/analytics";
import { VtoModalService } from "@hermes/fragments/vto-button";
import { Locale } from "@hermes/locale";

import { GridEdito } from "@hermes/states/grid-filters-state";
import { GRID_PAGE } from "@hermes/states/vto";

import { COUNTRY_LIST_OF_RETOUCHED_IMAGES } from "@hermes/utils-generic/constants";

import { EditoCTAClickEvent } from "../../events/edito-cta-click.event";

import { computeCTAToken, mapGridEditoToMedia } from "./grid-edito.helpers";

/**
 * Edito component to display medias from a FH campain
 */
@Component({
    selector: "h-grid-edito",
    templateUrl: "./grid-edito.component.html",
    styleUrls: ["./grid-edito.component.scss"],
    // false positive, we need to override drupal editorial HTML.
    // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridEditoComponent implements AfterViewInit, OnInit, OnDestroy {
    @Input({ required: true })
    public isEvenEdito!: boolean;

    public isContentEmpty = false;
    public media!: Media;
    public ctaToken?: CTAToken;

    private subscription = new Subscription();
    private prEdito!: GridEdito;

    constructor(
        @Inject(LOCALE) private locale: Locale,
        private analyticsService: AnalyticsService,
        private elementReference: ElementRef,
        private vtoModalService: VtoModalService,
    ) {}

    public get edito(): GridEdito {
        return this.prEdito;
    }
    @Input({ required: true })
    public set edito(edito: GridEdito) {
        this.prEdito = edito;
        this.media = mapGridEditoToMedia(this.edito);
        this.ctaToken = computeCTAToken(this.edito);
    }

    @HostListener("click", ["$event"])
    public onClick(event: Event): void {
        const target: HTMLElement = event.target as HTMLElement;
        // On click on link element, we throw an event for Analytics purpose
        if (this.isLinkTag(target)) {
            this.analyticsService.sendData(
                new EditoCTAClickEvent(
                    this.edito.contentTitle,
                    target.textContent ?? "",
                ),
            );
        } else if (this.isVTOCtaButtonTag(target)) {
            this.vtoModalService.openVtoModal(GRID_PAGE);
        }
    }

    public ngOnInit(): void {
        // Test if the edito content is empty so we don't append the div in the DOM
        if (this.edito) {
            this.isContentEmpty =
                this.edito.contentTitle === "" && this.edito.contentBody === "";
        }

        this.ctaToken = computeCTAToken(this.edito);
    }

    public ngAfterViewInit() {
        this.addIconOnVtoCta();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * @returns A boolean if the element in param is a link
     * @param element Element to test
     */
    public isLinkTag(element: HTMLElement): boolean {
        return element.nodeName.toLocaleLowerCase() === "a";
    }

    /**
     * Check if the country is into the array of "COUNTRY_LIST_OF_RETOUCHED_IMAGES"
     *
     * @returns true if the message "retouched image" have to be displayed
     */
    public isInCountryListOfRetouchedImages(): boolean {
        return COUNTRY_LIST_OF_RETOUCHED_IMAGES.includes(
            this.locale.countryCode,
        );
    }

    /**
     * Check if retouched-image div should be displayed
     *
     * @returns true if retouched-image have to be displayed
     */
    public showRetouchedImage(): boolean {
        return (
            this.edito.imageRetouched && this.isInCountryListOfRetouchedImages()
        );
    }

    /**
     * @returns A boolean if the element in param is VTO CTA button
     * @param element Element to test
     */
    public isVTOCtaButtonTag(element: HTMLElement): boolean {
        return element.parentElement
            ? element.parentElement.classList.contains("grid-vto-cta-button")
            : false;
    }

    /**
     * When VTO CTA contributed, we add an icon inside
     */
    private addIconOnVtoCta() {
        const ctaVTOAnchor: HTMLAnchorElement =
            this.elementReference.nativeElement.querySelector(
                ".grid-vto-cta-button a",
            );
        if (ctaVTOAnchor) {
            ctaVTOAnchor.insertAdjacentHTML(
                "afterbegin",
                '<span class="vto-cta-icon"></span>',
            );
        } else {
            const ctaVTOElement: HTMLElement =
                this.elementReference.nativeElement.querySelector(
                    ".grid-vto-cta-button",
                );
            if (ctaVTOElement) {
                const ctaVTOTextContent = ctaVTOElement.textContent;
                ctaVTOElement.innerHTML = `<button><span class="vto-cta-icon"></span>${ctaVTOTextContent}</button>`;
            }
        }
    }
}
